import { getAuthInstance } from '@zealth/core/firebase/runtime'
import { onIdTokenChanged, type User } from 'firebase/auth'
import { useCallback, useEffect, useState } from 'react'

import { type InsightsRole, resolveInsightsRole } from './insightsRole'

export type InsightsSession = {
  user: User | null
  /** Null while loading, and null for a signed-in account with no role. */
  role: InsightsRole | null
  isLoading: boolean
  /** True when the account's claims could not be read; `retry` tries again. */
  hasError: boolean
  retry: () => void
}

const LOADING = { user: null, role: null, isLoading: true, hasError: false }

/**
 * Who is signed in and whether they may see the dashboard.
 *
 * Subscribed to ID-TOKEN changes, not auth-state changes: the SDK renews the
 * token about hourly and fires this on every renewal, so a claim granted or
 * revoked while somebody is signed in reaches the page within the hour
 * (the same window the callable enforces) instead of on the next reload. The
 * token is force-refreshed once per account — on sign-in and on page load —
 * so a claim granted while signed out shows at once; a renewal event already
 * carries the new token, and forcing again there would fire this listener
 * again, a loop.
 *
 * Two failure modes are handled rather than left to hang the page on
 * "Loading…": a refresh that rejects (offline, token endpoint down) falls back
 * to the cached token and, failing that, to an error state with a retry; and
 * a slow refresh that completes after a newer auth event (sign-out, account
 * switch, unmount) is dropped instead of overwriting the newer state.
 */
export const useInsightsSession = (): InsightsSession => {
  const auth = getAuthInstance()
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<Omit<InsightsSession, 'retry'>>(LOADING)

  useEffect(() => {
    // Incremented on every token event and on cleanup; a resolution whose
    // number is no longer current belongs to a superseded event.
    let current = 0
    let knownUid: string | null = null

    const resolve = async (
      user: User,
      seq: number,
      force: boolean
    ): Promise<void> => {
      let claims: Record<string, unknown> | null = null
      try {
        claims = (await user.getIdTokenResult(force)).claims
      } catch {
        try {
          claims = force ? (await user.getIdTokenResult(false)).claims : null
        } catch {
          claims = null
        }
      }
      if (seq !== current) return
      setState(
        claims
          ? {
              user,
              role: resolveInsightsRole(claims),
              isLoading: false,
              hasError: false,
            }
          : { user, role: null, isLoading: false, hasError: true }
      )
    }

    const unsubscribe = onIdTokenChanged(auth, user => {
      const seq = ++current
      if (!user) {
        knownUid = null
        setState({ user: null, role: null, isLoading: false, hasError: false })
        return
      }
      const force = user.uid !== knownUid
      knownUid = user.uid
      void resolve(user, seq, force)
    })
    return (): void => {
      current++
      unsubscribe()
    }
  }, [auth, attempt])

  // Re-subscribing replays the current token, which re-reads the claims.
  const retry = useCallback((): void => {
    setState(LOADING)
    setAttempt(n => n + 1)
  }, [])

  return { ...state, retry }
}
